import { AnchorButton, Button } from "@svea/design-system-components-react";
import {
	Accordion,
	FormWithTracking,
	moneyFormatNoDecimal,
	Stack,
	Text,
	useText,
} from "@sveabank/cc-common";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router";
import Card from "../../../components/card";
import Content from "../../../components/content";
import PageNavigationFooter from "../../../components/page-navigation-footer";
import { withLoader } from "../../../hoc/with-loader";
import { usePageContext } from "../../../hooks/context";
import { usePages } from "../../../hooks/usePages";
import { applicationsApiSlice } from "../../../services/application";
import type { AppDispatch } from "../../../store";
import { stepIs } from "../../../utils/application";
import {
	calculateCredit,
	getCreditProductCostsSummary,
	setupInitialCreditConfig,
} from "../helpers";
import { useChoosePackage } from "../hooks";
import CreditConfigPageTable from "./credit-config-page-table";
import CreditConfigStatus from "./credit-config-status";
import CreditProductBlock from "./credit-product-block";
import CreditProductsRemovedModal from "./credit-products-removed-modal";

const CreditConfigPage = () => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const { nextPage, prevPage } = usePages();

	const text = useText();
	const [t, texts] = text;

	const {
		handleCreditProduct,
		configureCheckCredit,
		creditScore,
		selectedPackageId,
		steps,
		selectedCreditProducts,
		selectedPackageFee,
		checkCreditProductBrackets,
	} = usePageContext(useChoosePackage);

	const products = selectedCreditProducts.filter((product) => !product.isAddon);
	const addons = selectedCreditProducts.filter((product) => product.isAddon);
	const checkCredit = steps.find(stepIs("ConfigureCreditAccountStep"))?.state;
	const handleOnSave = async (values: Record<string, string>) => {
		const config = Object.keys(values).map((productId) => ({
			productId,
			creditLimit: Number(values[productId]),
		}));
		await handleCreditProduct(config);

		if (checkCredit) {
			await configureCheckCredit({
				productId: checkCredit.productId,
				accountId:
					checkCredit.selectedAccountId || checkCredit.availableAccountIds[0]?.accountId || "",
			});
		}

		dispatch(applicationsApiSlice.util.invalidateTags(["CurrentApplication"]));

		if (!nextPage) throw new Error("Tried navigating to nextPage without nextPage available");
		navigate(nextPage);
	};

	const validateForm = (values: Record<string, string>) => {
		const errors: Record<string, string> = {};
		const remainingCredit = calculateCredit(creditScore, values, selectedCreditProducts);
		if (remainingCredit < 0) {
			Object.keys(values).forEach((productId) => {
				errors[productId] = t(
					texts.adjustCredit.creditExceeded,
					moneyFormatNoDecimal(Math.abs(remainingCredit), true).toString(),
				);
			});
		}

		return errors;
	};

	if (!selectedPackageId || !selectedPackageFee) {
		return null;
	}

	return (
		<FormWithTracking<Record<string, string>>
			onSubmit={handleOnSave}
			initialValues={setupInitialCreditConfig(selectedCreditProducts, creditScore)}
			validate={validateForm}
			matomoInstance={window.Matomo}
			render={({ handleSubmit, values, submitting }) => {
				const remainingCredit = calculateCredit(creditScore, values, selectedCreditProducts);
				const costSummaryArray = getCreditProductCostsSummary(
					checkCreditProductBrackets,
					selectedPackageId,
					selectedPackageFee,
					selectedCreditProducts,
					addons,
					values,
				);

				const totalCost = costSummaryArray.reduce((acc, curr) => acc + curr[2], 0);

				return (
					<Content size={12} my={8}>
						<Stack gap={8}>
							{selectedCreditProducts.length > 1 && (
								<CreditConfigStatus creditScore={creditScore} remainingCredit={remainingCredit} />
							)}
							<Stack gap={8}>
								<form
									name="creditConfigForm"
									id="creditConfig"
									onSubmit={handleSubmit}
									className="flex flex-col gap-10"
								>
									{!!products.length && selectedPackageId && (
										<CreditProductBlock
											products={products}
											creditScore={creditScore}
											title={t(texts.choosePackage[selectedPackageId].title)}
										/>
									)}

									{!!addons.length && (
										<CreditProductBlock
											products={addons}
											creditScore={creditScore}
											title={t(texts.adjustCredit.addons)}
										/>
									)}
								</form>
							</Stack>
							<Stack direction="row" justify="end">
								<div className="flex max-w-[426px] grow justify-end">
									<Card direction="col" fullWidth classNames="bg-background-tertiary">
										<Accordion>
											<Accordion.Heading>
												<Stack direction="row" justify="between">
													<Text bold>{t(texts.adjustCredit.costSummary.totalSum)}</Text>
													<Text bold className="text-xl">
														{t(texts.adjustCredit.costSummary.subCost, totalCost.toString())}
													</Text>
												</Stack>
											</Accordion.Heading>
											<Accordion.Content>
												<CreditConfigPageTable
													costSummaryArray={costSummaryArray}
													selectedPackageId={selectedPackageId}
												/>
											</Accordion.Content>
										</Accordion>
									</Card>
								</div>
							</Stack>
						</Stack>
						<PageNavigationFooter>
							<Button variant="primary" form="creditConfig" type="submit" isPending={submitting}>
								{t(texts.common.continue)}
							</Button>
							{prevPage && (
								<AnchorButton as={Link} variant="secondary" to={prevPage}>
									{t(texts.common.goBack)}
								</AnchorButton>
							)}
						</PageNavigationFooter>
						<CreditProductsRemovedModal isOpen={!selectedCreditProducts.length} />
					</Content>
				);
			}}
		/>
	);
};

export default withLoader(useChoosePackage, CreditConfigPage);
